"use client";

import { useMemo, useState } from "react";
import { ExternalLink } from "lucide-react";
import { cn } from "@repo/lib";
import { useI18n } from "@repo/ui/providers";
import {
  CLIENTS_WITH_PUBLIC_PLAYERS,
  PLATFORM_LABELS,
  type Metric,
  type PlatformClient,
  type StatsGameDetail,
} from "@/lib/stats-types";
import {
  formatAgo,
  formatCount,
  formatDate,
  formatDateTime,
  formatExact,
  formatPercent,
  reviewScore,
} from "./format";
import { TimeSeriesChart } from "./time-series-chart";

const SECTION =
  "text-xs font-semibold uppercase tracking-wider text-muted-foreground";

// Dict keys (www.stats.*).
const CHART_METRICS: { metric: Metric; label: string; valueLabel: string }[] = [
  {
    metric: "steam_ccu",
    label: "www.stats.chart.steamPlayers",
    valueLabel: "www.stats.chart.players",
  },
  {
    metric: "twitch_viewers",
    label: "www.stats.twitchViewers",
    valueLabel: "www.stats.chart.viewers",
  },
  {
    metric: "discord_online",
    label: "www.stats.chart.discordOnline",
    valueLabel: "www.stats.chart.online",
  },
];

function Tile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      {hint && (
        <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>
      )}
    </div>
  );
}

function Toggle<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-md border p-0.5 text-xs">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded px-2.5 py-1 transition-colors",
            value === o.value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function GameStatsView({ game }: { game: StatsGameDetail }) {
  const { t, locale } = useI18n();
  const s = game.summary;
  const available = CHART_METRICS.filter(
    (m) => (game.daily[m.metric]?.length ?? 0) > 0,
  );
  const [metric, setMetric] = useState<Metric>(
    available[0]?.metric ?? "steam_ccu",
  );
  const [range, setRange] = useState<"7d" | "all">("7d");
  const chartMeta = CHART_METRICS.find((m) => m.metric === metric)!;

  const points = useMemo(() => {
    const series = range === "7d" ? game.hourly[metric] : game.daily[metric];
    return (series ?? []).map((p) => ({ t: p.t, v: p.max }));
  }, [game, metric, range]);

  const trackingSince = useMemo(() => {
    const firsts = Object.values(game.daily)
      .map((series) => series?.[0]?.t)
      .filter((t): t is number => typeof t === "number");
    return firsts.length > 0 ? Math.min(...firsts) : null;
  }, [game.daily]);

  const platformLabel = (client: PlatformClient) =>
    client === "launcher"
      ? t("www.stats.platform.launcher")
      : PLATFORM_LABELS[client];

  const score = reviewScore(s.steamReviews, s.steamReviewPositive);
  const publicBuilds = game.builds.filter((b) => b.branch === "public");
  const otherBuilds = game.builds.filter((b) => b.branch !== "public");

  return (
    <div className="space-y-10">
      {/* Headline numbers */}
      <section className="space-y-3">
        <h2 className={SECTION}>{t("www.stats.detail.players")}</h2>
        <div className="grid max-md:grid-cols-2 gap-3 md:grid-cols-4">
          <Tile
            label={t("www.app.stats.playingNow")}
            value={formatExact(s.steamCcu, locale)}
            hint={game.steamAppId ? undefined : t("www.stats.notOnSteam")}
          />
          <Tile
            label={t("www.app.stats.peak24h")}
            value={formatExact(s.steamPeak24h, locale)}
          />
          <Tile
            label={t("www.stats.detail.peak30d")}
            value={formatExact(s.steamPeak30d, locale)}
          />
          <Tile
            label={t("www.app.stats.trend7d")}
            value={formatPercent(s.steamTrend7d, locale)}
            hint={t("www.stats.detail.trendHint")}
          />
        </div>
      </section>

      {/* Chart */}
      {available.length > 0 && (
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className={SECTION}>
              {t(chartMeta.label)} ·{" "}
              {range === "7d"
                ? t("www.stats.chart.hourlyPeak")
                : t("www.stats.chart.dailyPeak")}
            </h2>
            <div className="flex flex-wrap gap-2">
              {available.length > 1 && (
                <Toggle
                  options={available.map((m) => ({
                    value: m.metric,
                    label: t(m.label),
                  }))}
                  value={metric}
                  onChange={setMetric}
                />
              )}
              <Toggle
                options={[
                  { value: "7d", label: t("www.stats.chart.range7d") },
                  { value: "all", label: t("www.stats.chart.rangeAll") },
                ]}
                value={range}
                onChange={setRange}
              />
            </div>
          </div>
          <div className="rounded-lg border bg-card p-3">
            <TimeSeriesChart
              points={points}
              valueLabel={t(chartMeta.valueLabel)}
              hourly={range === "7d"}
            />
          </div>
          {trackingSince && (
            <p className="text-xs text-muted-foreground">
              {t("www.stats.chart.trackedSince", {
                vars: { date: formatDate(trackingSince, locale) },
              })}
            </p>
          )}
        </section>
      )}

      {/* Community */}
      <section className="space-y-3">
        <h2 className={SECTION}>{t("www.stats.detail.community")}</h2>
        <div className="grid max-md:grid-cols-2 gap-3 md:grid-cols-4">
          <Tile
            label={t("www.stats.detail.steamReviews")}
            value={
              score !== null
                ? new Intl.NumberFormat(locale, {
                    style: "percent",
                    maximumFractionDigits: 1,
                  }).format(score / 100)
                : "–"
            }
            hint={
              s.steamReviews
                ? t("www.stats.detail.reviewsHint", {
                    vars: { count: formatCount(s.steamReviews, locale) },
                  })
                : undefined
            }
          />
          <Tile
            label={t("www.stats.detail.steamFollowers")}
            value={formatCount(s.steamFollowers, locale)}
          />
          <Tile
            label={t("www.stats.twitchViewers")}
            value={formatCount(s.twitchViewers, locale)}
            hint={
              s.twitchChannels !== null
                ? t("www.stats.detail.liveChannels", {
                    vars: { count: formatCount(s.twitchChannels, locale) },
                  })
                : undefined
            }
          />
          <Tile
            label={t("www.stats.detail.officialDiscord")}
            value={formatCount(s.discordOnline, locale)}
            hint={
              s.discordMembers !== null
                ? t("www.stats.detail.discordHint", {
                    vars: { count: formatCount(s.discordMembers, locale) },
                  })
                : game.discordInvite
                  ? undefined
                  : t("www.stats.detail.notTracked")
            }
          />
        </div>
      </section>

      {/* Platforms */}
      <section className="space-y-3">
        <h2 className={SECTION}>{t("www.stats.detail.platforms")}</h2>
        {game.platforms.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("www.stats.detail.noPlatforms")}
          </p>
        ) : (
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">
                    {t("www.stats.detail.platform")}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t("www.stats.detail.playerNumbers")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {game.platforms.map((p) => {
                  const isPublic = CLIENTS_WITH_PUBLIC_PLAYERS.includes(
                    p.client,
                  );
                  return (
                    <tr key={p.client} className="border-t">
                      <td className="px-3 py-2">
                        {p.url ? (
                          <a
                            href={p.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 hover:text-primary"
                          >
                            {platformLabel(p.client)}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          platformLabel(p.client)
                        )}
                        {p.status === "upcoming" && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {t("www.stats.detail.upcoming")}
                          </span>
                        )}
                        {p.status === "early_access" && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {t("www.stats.detail.earlyAccess")}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 tabular-nums">
                        {isPublic ? (
                          s.steamCcu !== null ? (
                            t("www.stats.detail.playingNowCount", {
                              vars: { count: formatExact(s.steamCcu, locale) },
                            })
                          ) : (
                            <span className="text-muted-foreground">
                              {t("www.stats.detail.noPlayers")}
                            </span>
                          )
                        ) : (
                          <span className="text-muted-foreground">
                            {t("www.stats.detail.notPublished")}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {game.platforms.some(
          (p) => !CLIENTS_WITH_PUBLIC_PLAYERS.includes(p.client),
        ) && (
          <p className="text-xs text-muted-foreground">
            {t("www.stats.detail.platformsNote")}
          </p>
        )}
      </section>

      {/* Patches */}
      {game.builds.length > 0 && (
        <section className="space-y-3">
          <h2 className={SECTION}>{t("www.stats.detail.patchHistory")}</h2>
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">
                    {t("www.stats.detail.updated")}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t("www.stats.detail.branch")}
                  </th>
                  <th className="px-3 py-2 font-medium">
                    {t("www.stats.detail.build")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...publicBuilds, ...otherBuilds].slice(0, 20).map((b) => (
                  <tr key={`${b.branch}-${b.buildId}`} className="border-t">
                    <td
                      className="px-3 py-2"
                      title={formatDateTime(b.timeUpdated ?? b.seenAt, locale)}
                      suppressHydrationWarning
                    >
                      {formatAgo(b.timeUpdated ?? b.seenAt, locale)}
                    </td>
                    <td className="px-3 py-2">{b.branch}</td>
                    <td className="px-3 py-2 font-mono text-xs tabular-nums">
                      {b.buildId}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
